import "./CheckoutForm.scss";

import Callout from "./Callout";
import OrderReviewTable from "./OrderReviewTable";
import { setUserStore, userStore } from "../stores/user.store";
import { orderStore, sendOrder } from "../stores/order.store";
import OrderSuccess from "./OrderSuccess";
import PickupSlotPicker from "./PickupSlotPicker";
import { cartStore } from "../stores/cart.store";
import { Loader } from "./Loader";
import { createSignal, onMount } from "solid-js";
import { pickupSchedule } from "../stores/shop.store";
import { toUtcMidnight } from "../../shared/date-utils";
import { isAvailableSlot } from "../../shared/pickup-slots";

function CheckoutForm() {
  const [formIsValid, setFormIsValid] = createSignal(true);
  let pickupInput: HTMLInputElement;

  // Form configuration with validation rules and functions
  const formConfig = {
    name: { 
      required: true, 
      validate: (value) => !value.trim() ? "Dit veld is verplicht" : null
    },
    lastname: { 
      required: true, 
      validate: (value) => !value.trim() ? "Dit veld is verplicht" : null
    },
    telephone: { 
      required: true, 
      validate: (value) => {
        if (!value.trim()) return "Dit veld is verplicht";
        if (!/^\d{10}$/.test(value)) return "Voer een 10-cijferig telefoonnummer in";
        return null;
      }
    },
    email: { 
      required: true, 
      validate: (value) => {
        if (!value.trim()) return "Dit veld is verplicht";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Voer een geldig e-mailadres in";
        return null;
      }
    },
    // date and time are chosen together in the PickupSlotPicker
    pickupDate: {
      required: true,
      validate: () => {
        if (!userStore.pickupDate || !userStore.pickupTime) return "Kies een afhaalmoment";
        if (!isAvailableSlot(pickupSchedule(), userStore.pickupDate, userStore.pickupTime, toUtcMidnight(new Date())))
          return "Dit afhaalmoment is niet meer beschikbaar, kies een ander";
        return null;
      },
    },
    // Company conditional fields
    streetAndNumber: { 
      requiredIf: () => !!userStore.company, 
      validate: (value) => !value.trim() ? "Dit veld is verplicht" : null
    },
    city: { 
      requiredIf: () => !!userStore.company, 
      validate: (value) => !value.trim() ? "Dit veld is verplicht" : null
    },
    postcode: { 
      requiredIf: () => !!userStore.company, 
      validate: (value) => {
        if (!value.trim()) return "Dit veld is verplicht";
        if (!/^\d{4}$/.test(value)) return "Voer een geldige Belgische postcode in";
        return null;
      }
    },
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;

    // Check form validity before submitting
    if (checkFormValidity(form)) {
      await sendOrder();
    }
  };

  function checkFormValidity(form: HTMLFormElement) {
    let isValid = true;
    
    // Validate all fields based on configuration
    Object.entries(formConfig).forEach(([fieldName, config]) => {
      const isFieldRequired = ('required' in config && config.required) || ('requiredIf' in config && config.requiredIf && config.requiredIf());
      
      if (isFieldRequired) {
        const element = form.querySelector(`[name="${fieldName}"]`) as HTMLInputElement | HTMLSelectElement;
        
        if (element) {
          let errorMessage = validateInput(element, config);
         
          if (errorMessage) {
            isValid = false;
            // Display the error
            const container = element.parentElement;
            const errorElement = container.getElementsByClassName("inputErrorMessage")?.[0] as HTMLElement;
            container.classList.add("input__error");
            if (errorElement) errorElement.innerHTML = errorMessage;
          }
        }
      }
    });
    
    setFormIsValid(isValid);
    return isValid;
  }

  const storeInput = (e: InputEvent) => {
    const { value, name } = e.currentTarget as HTMLInputElement;
    setUserStore({ ...userStore, [name]: value });
    
    // Manual validation on input
    validateInput(e.currentTarget as HTMLInputElement);
  };

  const validateInput = (element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, config = null) => {
    const { name, value } = element;
    const container = element.parentElement;
    const errorElement = container.getElementsByClassName("inputErrorMessage")?.[0] as HTMLElement;
    
    // Clear previous error
    container.classList.remove("input__error");
    if (errorElement) errorElement.innerHTML = "";
    
    let errorMessage = null;
    
    if (config) {
      // Use config-based validation for form submission
      const isRequired = config.required || (config.requiredIf && config.requiredIf());
      
      if (isRequired || value) {
        errorMessage = config.validate(value);
      }
    } else {
      // Fallback for real-time validation (onInput)
      const fieldConfig = formConfig[name];
      if (fieldConfig && fieldConfig.validate) {
        errorMessage = fieldConfig.validate(value);
      }
    }
    
    if (errorMessage) {
      container.classList.add("input__error");
      if (errorElement) errorElement.innerHTML = errorMessage;
      return errorMessage;
    }
    
    return null;
  };

  // a pickup moment has to be chosen again for every order
  onMount(() => {
    setUserStore({ ...userStore, pickupDate: "", pickupTime: "" });
  });

  return (
    <>
      {orderStore.state === "success" && <OrderSuccess />}
      {orderStore.state !== "success" && cartStore.cartItems.length > 0 && (
        <form
          onSubmit={handleSubmit}
          noValidate
          class={
            "checkoutForm " +
            (orderStore.state === "sending" ? "checkoutForm__sending" : "")
          }
        >
          <h2>Jouw gegevens</h2>
          <div class="checkoutForm_col">
            <div class="input">
              <label for="name" class="block">
                Voornaam *
              </label>
              <input
                id="name"
                name="name"
                value={userStore.name}
                onInput={storeInput}
              />
              <div class="inputErrorMessage">A</div>
            </div>

            <div class="input">
              <label for="lastname" class="block">
                Achternaam *
              </label>
              <input
                id="lastname"
                name="lastname"
                value={userStore.lastname}
                onInput={storeInput}
              />
              <div class="inputErrorMessage">A</div>
            </div>
          </div>

          <div class="input">
            <label for="company" class="block">
              Bedrijfsnaam (optioneel)
            </label>
            <input
              id="company"
              name="company"
              value={userStore.company}
              onInput={storeInput}
            />
            <div class="inputErrorMessage">A</div>
          </div>

          {userStore.company && (
            <div class="input">
              <label for="taxNr" class="block">
                Btw nummer
              </label>
              <input
                id="taxNr"
                name="taxNr"
                value={userStore.taxNr}
                onInput={storeInput}
              />
              <div class="inputErrorMessage">A</div>
            </div>
          )}

          {userStore.company && (
            <div class="input">
              <label for="streetAndNumber" class="block">
                Straat en huisnummer *
              </label>
              <input
                id="streetAndNumber"
                name="streetAndNumber"
                value={userStore.streetAndNumber}
                onInput={storeInput}
              />
              <div class="inputErrorMessage">A</div>
            </div>
          )}

          {userStore.company && (
            <div class="checkoutForm_col">
              <div class="input">
                <label for="city" class="block">
                  Plaats *
                </label>
                <input
                  id="city"
                  name="city"
                  value={userStore.city}
                  onInput={storeInput}
                />
                <div class="inputErrorMessage">A</div>
              </div>

              <div class="input">
                <label for="postcode" class="block">
                  Postcode *
                </label>
                <input
                  id="postcode"
                  name="postcode"
                  value={userStore.postcode}
                  onInput={storeInput}
                  title="Voer een geldige Belgische postcode in"
                />
                <div class="inputErrorMessage">A</div>
              </div>
            </div>
          )}

          <div class="input">
            <label for="telephone" class="block">
              Telefoon *
            </label>
            <input
              id="telephone"
              name="telephone"
              value={userStore.telephone}
              onInput={storeInput}
              title="Voer een 10-cijferig telefoonnummer in"
            />
            <div class="inputErrorMessage">A</div>
          </div>

          <div class="input">
            <label for="email" class="block">
              E-mailadres *
            </label>
            <input
              id="email"
              name="email"
              value={userStore.email}
              onInput={storeInput}
              title="Voer een geldige email adres in"
            />
            <div class="inputErrorMessage">A</div>
          </div>

          <div class="input">
            <label id="pickupLabel">
              Wanneer zou je jouw plantgoed willen afhalen? *
            </label>
            <div role="group" aria-labelledby="pickupLabel">
              <PickupSlotPicker
                date={userStore.pickupDate}
                time={userStore.pickupTime}
                onSelect={(pickupDate, pickupTime) => {
                  setUserStore({ ...userStore, pickupDate, pickupTime });
                  validateInput(pickupInput);
                }}
              />
            </div>
            <input type="hidden" ref={pickupInput} name="pickupDate" value={userStore.pickupDate} />
            <div class="inputErrorMessage">A</div>
          </div>

          <div class="input">
            <label for="message" class="block">
              Bericht
            </label>
            <textarea
              id="message"
              name="message"
              value={userStore.message}
              onInput={storeInput}
              rows="4"
              maxLength={500}
            ></textarea>
            <div class="inputErrorMessage">A</div>
          </div>

          <div>
            <h2>Jouw bestelling</h2>
            <OrderReviewTable />
          </div>

          <Callout type="info" title="Opgelet">
            <span>
              Plantgoed wordt altijd afgehaald aan de kwekerij, wij versturen
              niet per post.
              <br />
              Betalen kan bij afhaling contant of met de bankkaart.
              <br />
              Breng een doos of bak mee om je plantgoed te vervoeren.
            </span>
          </Callout>

          <p>
            Je persoonlijke gegevens zullen worden gebruikt om je bestelling te
            verwerken en om je beleving op deze website te optimaliseren.
          </p>

          {!formIsValid() && (
            <Callout type="warning">
              <span>
                Kijk de gegevens in het formulier na. Zorg ervoor dat je alle
                gegevens correct invult.
              </span>
            </Callout>
          )}

          <div class="checkoutForm_col">
            <div></div>
            <button type="submit" class="checkoutForm_submit btn btn__primary">
              {orderStore.state === "sending" && <Loader color="white" />}
              Bestelling plaatsen
            </button>
          </div>
        </form>
      )}
      {orderStore.state !== "success" && cartStore.cartItems.length === 0 && (
        <>
          <h1>Jouw kruiwagen</h1>
          <div class="cart_empty">
            <Callout>Jouw kruiwagen is leeg</Callout>
            <a href="/" class="btn btn__primary">
              ga naar de producten
            </a>
          </div>
        </>
      )}
    </>
  );
}

export default CheckoutForm;
